import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import PlaceIcon from '@material-ui/icons/Place';
import { RootState } from '../../../../../redux/root';
import { Loader } from '../../../../../atoms';
import { ClientActivityComment } from '../../../types';

type StatusTone = 'green' | 'blue' | 'amber' | 'red' | 'grey';

const statusPalette = {
  green: { bg: '#DCFCE7', fg: '#166534', border: '#C5E7D2' },
  blue: { bg: '#E9F2FF', fg: '#1D4ED8', border: '#CFE6F3' },
  amber: { bg: '#FFF1D6', fg: '#92600A', border: '#FFE7AC' },
  red: { bg: '#FEE2E2', fg: '#B91C1C', border: '#FBCFCF' },
  grey: { bg: 'var(--neutral-100)', fg: 'var(--neutral-600)', border: 'var(--border)' },
};

const toneFor = (status: string): StatusTone => {
  const s = (status || '').toLowerCase();
  if (/complete|approved|issued|paid/.test(s)) return 'green';
  if (/progress|review|submitted/.test(s)) return 'blue';
  if (/pending|waiting|hold|sent|outstanding/.test(s)) return 'amber';
  if (/denied|rejected|overdue|void/.test(s)) return 'red';
  return 'grey';
};

const fullName = (first?: string | null, last?: string | null) =>
  [first, last].filter(Boolean).join(' ');

// "Aaron Turner - test9@test.com", dropping whichever half is missing.
const contactLabel = (first?: string | null, last?: string | null, email?: string | null) =>
  [fullName(first, last), email].filter(Boolean).join(' - ');

// The client endpoint (client_projects.GetPermitActivities) only ever returns
// these three activity types -- its CLIENT_ACTIVITY_TYPES whitelist is
// ['Permitting', 'Proposal', 'Reviews']. The backend title is plural; the label
// stays singular to match the rest of the client UI.
const ACTIVITY_TYPE_FILTERS: { label: string; value: string }[] = [
  { label: 'All', value: 'All' },
  { label: 'Proposal', value: 'Proposal' },
  { label: 'Permitting', value: 'Permitting' },
  { label: 'Review', value: 'Reviews' },
];

const matchesTypeFilter = (activityTypeName: string | null | undefined, filter: string) =>
  filter === 'All' || (activityTypeName || '').trim().toLowerCase() === filter.toLowerCase();

const Field = ({ label, value }: { label: string; value?: string | null }) => (
  <FieldBlock>
    <FieldLabel>{label}</FieldLabel>
    <FieldValue title={value || ''}>{value || '—'}</FieldValue>
  </FieldBlock>
);

const yesNo = (value: boolean) => (value ? 'Yes' : 'No');

const Comments = ({ comments }: { comments: ClientActivityComment[] }) => (
  <CommentsPane>
    <PaneTitle>
      <span>Comments</span>
      <CountPill>{comments.length}</CountPill>
    </PaneTitle>
    {comments.length === 0 ? (
      <NoComments>No comments on this activity.</NoComments>
    ) : (
      <CommentList>
        {comments.map(c => (
          <CommentCard key={c.id}>
            {/* Comment bodies are rich text authored in the employee app; the
                same dangerouslySetInnerHTML rendering is used by Common/CommentsTable. */}
            <CommentText dangerouslySetInnerHTML={{ __html: c.text }} />
            <CommentMeta>
              <CommentAuthor>{c.username || 'Unknown'}</CommentAuthor>
              <MetaDot>·</MetaDot>
              <span>{c.createdAt}</span>
              {c.visibleFilename && (
                <>
                  <MetaDot>·</MetaDot>
                  <span>{c.visibleFilename}</span>
                </>
              )}
            </CommentMeta>
          </CommentCard>
        ))}
      </CommentList>
    )}
  </CommentsPane>
);

const ActivityItems = () => {
  const activities = useSelector((s: RootState) => s.clientActivities.PermitActivities);
  const permitActivitiesLoading = useSelector(
    (s: RootState) => s.clientActivities.permitActivitiesLoading,
  );
  const SelectedActivityPermit = useSelector(
    (s: RootState) => s.clientActivities.SelectedActivityPermit,
  );

  const [typeFilter, setTypeFilter] = React.useState('All');

  // Rows on the Activities list carry permitId/namePermitName, which the shared
  // InspectionItemType doesn't declare -- same reason namePermitName is @ts-ignore'd below.
  const selectedPermitId = (SelectedActivityPermit as any)?.permitId;

  // The modal unmounts on close, but the permit can also be swapped underneath
  // it from the dashboard shortcut -- start each permit back on "All".
  React.useEffect(() => {
    setTypeFilter('All');
  }, [selectedPermitId]);

  const visibleActivities = React.useMemo(
    () => activities.filter(item => matchesTypeFilter(item.activityTypeName, typeFilter)),
    [activities, typeFilter],
  );

  const activeFilterLabel =
    ACTIVITY_TYPE_FILTERS.find(o => o.value === typeFilter)?.label || typeFilter;

  return (
    <Root>
      <HeaderCard>
        <HeaderCol>
          <HeaderLabel>Project / Permit</HeaderLabel>
          {/* @ts-ignore */}
          <HeaderTitle title={SelectedActivityPermit?.namePermitName || ''}>
            {/* @ts-ignore */}
            {SelectedActivityPermit?.namePermitName || '—'}
          </HeaderTitle>
        </HeaderCol>
        <HeaderDivider />
        <HeaderCol>
          <HeaderLabel>Address</HeaderLabel>
          <HeaderAddress title={SelectedActivityPermit?.address || ''}>
            <PlaceIcon style={{ fontSize: 16 }} />
            <span>{SelectedActivityPermit?.address || '—'}</span>
          </HeaderAddress>
        </HeaderCol>
      </HeaderCard>

      <SectionBar>
        <SectionEyebrow>
          <span>Activities</span>
          <ActivitiesCount>{visibleActivities.length}</ActivitiesCount>
        </SectionEyebrow>
        <FilterControl>
          <FilterLabel htmlFor="activity-type-filter">Filter:</FilterLabel>
          <FilterSelect
            id="activity-type-filter"
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
          >
            {ACTIVITY_TYPE_FILTERS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </FilterSelect>
        </FilterControl>
      </SectionBar>

      {permitActivitiesLoading && activities.length === 0 ? (
        <LoadingWrap>
          <Loader size={32} thickness={3.5} />
          <LoadingLabel>Loading activities…</LoadingLabel>
        </LoadingWrap>
      ) : visibleActivities.length === 0 ? (
        <Empty>
          {activities.length === 0
            ? 'No activities on this permit.'
            : `No ${activeFilterLabel} activities on this permit.`}
        </Empty>
      ) : (
        <Rows>
          {visibleActivities.map(item => (
            <RowCard key={item.id}>
              <RowHeader>
                <RowHeaderLeft>
                  <TypeChip>{item.activityTypeName || '—'}</TypeChip>
                  <RowTitle title={item.activityTitleName || ''}>
                    {item.activityTitleName || '—'}
                  </RowTitle>
                </RowHeaderLeft>
                <RowHeaderRight>
                  {item.isEmergency && <EmergencyChip>Emergency</EmergencyChip>}
                  {item.statusName && (
                    <StatusChip tone={toneFor(item.statusName)}>{item.statusName}</StatusChip>
                  )}
                </RowHeaderRight>
              </RowHeader>

              <RowBody>
                <Fields>
                  <Field label="Activity Type" value={item.activityTypeName} />
                  <Field label="Activity Title" value={item.activityTitleName} />
                  <Field label="Activity Status" value={item.statusName} />
                  <Field
                    label="Activity Contact"
                    value={contactLabel(
                      item.contactFirstName,
                      item.contactLastName,
                      item.contactEmail,
                    )}
                  />
                  <Field label="Follow Up Date" value={item.followUp} />
                  <Field
                    label="Assigned To"
                    value={fullName(item.assignedToFirstName, item.assignedToLastName)}
                  />
                  <FullWidth>
                    <Field label="Information" value={item.information} />
                  </FullWidth>
                  <Field label="Date Created" value={item.createdAt} />
                  <Field label="Created By" value={item.createdBy} />
                  <Field label="Emergency" value={yesNo(item.isEmergency)} />
                  <Field label="Activity completed" value={yesNo(item.isCompleted)} />
                  {item.isCompleted && (
                    <>
                      <Field label="Date Completed" value={item.completedAt} />
                      <Field label="Completed By" value={item.completedBy} />
                    </>
                  )}
                </Fields>

                <Comments comments={item.comments || []} />
              </RowBody>
            </RowCard>
          ))}
        </Rows>
      )}
    </Root>
  );
};

export default ActivityItems;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont,
    'Helvetica Neue', Arial, Roboto, sans-serif;
`;

const HeaderCard = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 1px minmax(0, 1fr);
  gap: 24px;
  align-items: stretch;
  padding: 18px 20px;
  background: var(--neutral-50);
  border: 1px solid var(--border);
  border-radius: 10px;

  @media only screen and (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 14px;
  }
`;

const HeaderCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const HeaderDivider = styled.div`
  background: var(--border);
  width: 1px;

  @media only screen and (max-width: 640px) {
    display: none;
  }
`;

const HeaderLabel = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const HeaderTitle = styled.span`
  font-size: 1.0625rem;
  font-weight: 700;
  color: var(--mainText);
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HeaderAddress = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.9375rem;
  color: var(--neutral-700);
  line-height: 1.35;
  min-width: 0;

  svg {
    color: var(--neutral-500);
    flex-shrink: 0;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const SectionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const FilterControl = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const FilterLabel = styled.label`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--neutral-600);
  white-space: nowrap;
`;

const FilterSelect = styled.select`
  height: 32px;
  min-width: 150px;
  padding: 0 10px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  font-family: inherit;
  font-size: 0.8125rem;
  color: var(--mainText);
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;

  &:hover {
    border-color: var(--neutral-400);
  }

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: var(--shadow-focus);
  }
`;

const SectionEyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500);
  padding: 0 2px;
`;

const ActivitiesCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 20px;
  padding: 0 8px;
  border-radius: 999px;
  background: var(--primary-50);
  color: var(--primary-700);
  border: 1px solid var(--primary-100, #cfe6f3);
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0;
`;

/* One activity per full-width row, rather than a multi-column tile grid. */
const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const RowCard = styled.div`
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-xs);
  overflow: hidden;
`;

const RowHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 12px 16px;
  background: var(--neutral-50);
  border-bottom: 1px solid var(--border);
`;

const RowHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const RowHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const RowTitle = styled.span`
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--mainText);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TypeChip = styled.span`
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 10px;
  border-radius: 999px;
  background: var(--neutral-100);
  color: var(--neutral-600);
  border: 1px solid var(--border);
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  flex-shrink: 0;
`;

const StatusChip = styled.span<{ tone: StatusTone }>`
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: 700;
  background: ${({ tone }) => statusPalette[tone].bg};
  color: ${({ tone }) => statusPalette[tone].fg};
  border: 1px solid ${({ tone }) => statusPalette[tone].border};
`;

const EmergencyChip = styled.span`
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: 700;
  background: ${statusPalette.red.bg};
  color: ${statusPalette.red.fg};
  border: 1px solid ${statusPalette.red.border};
`;

/* Fields left, comments right -- the same split as the employee edit modal. */
const RowBody = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(0, 1fr);
  gap: 20px;
  padding: 16px;

  @media only screen and (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const Fields = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px 20px;
  align-content: start;

  @media only screen and (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const FullWidth = styled.div`
  grid-column: 1 / -1;
  min-width: 0;
`;

const FieldBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

const FieldLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--neutral-500);
`;

const FieldValue = styled.span`
  font-size: 0.875rem;
  color: var(--mainText);
  line-height: 1.4;
  word-break: break-word;
`;

const CommentsPane = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  padding: 14px;
  background: var(--neutral-50);
  border: 1px solid var(--border);
  border-radius: 10px;
`;

const PaneTitle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const CountPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 18px;
  padding: 0 7px;
  border-radius: 999px;
  background: var(--primary-50);
  color: var(--primary-700);
  border: 1px solid var(--primary-100, #cfe6f3);
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0;
`;

const CommentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 260px;
  overflow-y: auto;
`;

const CommentCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 12px;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 8px;
`;

const CommentText = styled.div`
  font-size: 0.8125rem;
  color: var(--neutral-700);
  line-height: 1.45;
  word-break: break-word;

  p {
    margin: 0 0 6px;
  }

  p:last-child {
    margin-bottom: 0;
  }

  img {
    max-width: 100%;
    height: auto;
  }
`;

const CommentMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  font-size: 0.75rem;
  color: var(--neutral-500);
`;

const CommentAuthor = styled.span`
  font-weight: 600;
  color: var(--primary-700);
`;

const MetaDot = styled.span`
  color: var(--neutral-400);
`;

const NoComments = styled.div`
  padding: 16px 8px;
  text-align: center;
  font-size: 0.8125rem;
  color: var(--neutral-500);
`;

const Empty = styled.div`
  padding: 40px 16px;
  text-align: center;
  color: var(--neutral-500);
  font-size: 0.875rem;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
`;

const LoadingWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 48px 16px;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
`;

const LoadingLabel = styled.div`
  font-size: 0.875rem;
  color: var(--neutral-500);
  font-weight: 500;
`;
